import React, { useEffect, useState } from "react";
import {
  getSubscriptionPlans,
  createCheckoutSession,
  getPublicConfig,
} from "../../services/GameService";
import "./PricingPage.css";

const intervalLabel = (interval) =>
  interval === "year" ? "/year" : interval === "month" ? "/month" : "";

// Pricing page: a free tier plus the paid plans from the API. Subscribing
// starts a Stripe Checkout Session and redirects to the hosted page.
const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyPlan, setBusyPlan] = useState(null);
  // null = unknown yet, true/false once the public config has loaded.
  const [paymentsEnabled, setPaymentsEnabled] = useState(null);

  useEffect(() => {
    getPublicConfig()
      .then((cfg) => setPaymentsEnabled(cfg.payments_enabled !== false))
      // On error, assume payments are on so we never wrongly hide the plans.
      .catch(() => setPaymentsEnabled(true));
  }, []);

  useEffect(() => {
    // Skip the plans fetch entirely when payments are disabled.
    if (paymentsEnabled === false) {
      setLoading(false);
      return;
    }
    if (paymentsEnabled === null) return;
    getSubscriptionPlans()
      .then(setPlans)
      .catch(() => setError("Could not load plans."))
      .finally(() => setLoading(false));
  }, [paymentsEnabled]);

  const subscribe = async (planId) => {
    setBusyPlan(planId);
    setError("");
    try {
      const { url } = await createCheckoutSession(planId);
      if (url) {
        window.location.href = url;
      }
    } catch (e) {
      setError(
        e.response?.data?.error || "Could not start checkout. Please try again."
      );
    } finally {
      setBusyPlan(null);
    }
  };

  // Paywall turned off (lab/pilot): nothing to sell.
  if (paymentsEnabled === false) {
    return (
      <div className="pricing-page">
        <h1 className="pricing-title">Everything's unlocked</h1>
        <p className="pricing-sub">
          All games and levels are free right now — just log in to play. No
          subscription needed.
        </p>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <h1 className="pricing-title">Choose your plan</h1>
      <p className="pricing-sub">
        Start free, upgrade any time. Cancel whenever you like.
      </p>

      {error && (
        <p className="pricing-error" role="alert">
          {error}
        </p>
      )}

      <div className="pricing-grid">
        <div className="pricing-card">
          <h2 className="pricing-card-name">Free</h2>
          <p className="pricing-card-price">$0</p>
          <p className="pricing-card-desc">
            A starter set of games and your basic progress — no card needed.
          </p>
          <button className="pricing-btn pricing-btn-current" disabled>
            Always free
          </button>
        </div>

        {loading ? (
          <p>Loading plans…</p>
        ) : (
          plans.map((plan) => (
            <div className="pricing-card pricing-card-paid" key={plan.id}>
              <h2 className="pricing-card-name">{plan.name}</h2>
              <p className="pricing-card-price">
                ${plan.price}
                <span className="pricing-interval">
                  {intervalLabel(plan.interval)}
                </span>
              </p>
              <p className="pricing-card-desc">
                Every game, full progress history, and the voice-guided
                exercises.
              </p>
              <button
                className="pricing-btn pricing-btn-primary"
                onClick={() => subscribe(plan.id)}
                disabled={busyPlan === plan.id}
              >
                {busyPlan === plan.id ? "Redirecting…" : "Subscribe"}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PricingPage;
